import type {
  CreatedExternalService,
  ExternalServiceDto,
  ExternalServiceTokenDto,
  IssuedExternalServiceToken,
} from "@/types/externalService";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readId(value: unknown): number | null {
  const id = Number(value);
  return Number.isFinite(id) ? Math.trunc(id) : null;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function normalizeExternalServiceToken(
  raw: unknown,
): ExternalServiceTokenDto | null {
  const item = asRecord(raw);
  if (!item) return null;

  const id = readId(item.id);
  const prefix = readString(item.prefix ?? item.token_prefix);
  if (id == null || !prefix) return null;

  return {
    id,
    name: readString(item.name),
    prefix,
    lastUsedAt: readString(item.lastUsedAt ?? item.last_used_at),
    revokedAt: readString(item.revokedAt ?? item.revoked_at),
    createdAt: readString(item.createdAt ?? item.created_at),
  };
}

export function normalizeExternalService(
  raw: unknown,
): ExternalServiceDto | null {
  const item = asRecord(raw);
  if (!item) return null;

  const id = readId(item.id);
  const name = readString(item.name);
  if (id == null || !name) return null;

  const rawTokens = Array.isArray(item.tokens) ? item.tokens : [];
  const formsCount = Number(item.formsCount ?? item.forms_count ?? 0);

  return {
    id,
    name,
    description: readString(item.description),
    formsCount: Number.isFinite(formsCount) ? Math.max(0, formsCount) : 0,
    tokens: rawTokens
      .map(normalizeExternalServiceToken)
      .filter((token): token is ExternalServiceTokenDto => token != null),
    createdAt: readString(item.createdAt ?? item.created_at),
    updatedAt: readString(item.updatedAt ?? item.updated_at),
  };
}

export function normalizeExternalServices(raw: unknown): ExternalServiceDto[] {
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeExternalService)
      .filter((service): service is ExternalServiceDto => service != null);
  }

  const record = asRecord(raw);
  return record && "data" in record
    ? normalizeExternalServices(record.data)
    : [];
}

export function normalizeCreatedExternalService(
  raw: unknown,
): CreatedExternalService | null {
  const record = asRecord(raw);
  if (!record) return null;

  const service = normalizeExternalService(record.service);
  const token = readString(record.token);
  return service && token ? { service, token } : null;
}

export function normalizeIssuedExternalServiceToken(
  raw: unknown,
): IssuedExternalServiceToken | null {
  const record = asRecord(raw);
  if (!record) return null;

  const token = readString(record.token);
  if (!token) return null;

  return {
    credential: normalizeExternalServiceToken(record.credential),
    token,
  };
}
